/**
 * useVisualViewportHeight — the height (px) of the *visual* viewport, i.e. the
 * area actually visible to the user, EXCLUDING the on-screen keyboard.
 *
 * Why this exists
 * ───────────────
 * On iOS Safari the software keyboard does NOT shrink the layout viewport, so
 * CSS `vh`/`dvh`/`svh` units (and the position of bottom-anchored `fixed`
 * elements) stay at their full no-keyboard size when the keyboard is up. A
 * full-screen sheet sized in `dvh` therefore runs its lower half BEHIND the
 * keyboard, and a bottom-anchored sheet gets dragged up — clipping its top —
 * when Safari scrolls a focused input into view. `window.visualViewport.height`
 * is the only value that tracks the keyboard, so a panel sized to it sits
 * exactly in the space above the keyboard.
 *
 * Stability
 * ─────────
 * We listen to `resize` ONLY — never `scroll`. The documented failed attempts
 * (`.claude/skills/ios-safari-keyboard/FAILED-ATTEMPTS.md`) jittered because
 * they tracked `scroll` to re-anchor a bottom-fixed element on every touch.
 * A TOP-anchored panel sized by `resize` alone has no such feedback loop.
 *
 * `enabled`
 * ─────────
 * Pass `enabled=false` to fully detach — no `resize` listener, no re-renders —
 * while the consumer doesn't need the height (e.g. a picker sheet that's
 * closed). A `resize` listener that stays live would re-render the consumer on
 * every keyboard open/close and orientation change even when its value is
 * unused; gating it keeps the cost at exactly zero until the panel opens. Flips
 * back on (and re-syncs the current height) the moment `enabled` is true again.
 *
 * Returns `null` when there is no `visualViewport` (SSR, or a browser without
 * the API) or while disabled, so the caller can fall back to a CSS `dvh`/`%`
 * height.
 */

import { useEffect, useState } from 'react';

function readViewportHeight(): number | null {
  if (typeof window === 'undefined' || !window.visualViewport) return null;
  return window.visualViewport.height;
}

export function useVisualViewportHeight(enabled = true): number | null {
  const [height, setHeight] = useState<number | null>(() =>
    enabled ? readViewportHeight() : null,
  );

  useEffect(() => {
    if (!enabled) {
      // Honour the documented contract — null while disabled — so a caller that
      // gates on the return value sees the true disabled state, not a stale
      // height. Runs once on the enabled→false transition, not per render.
      setHeight(null);
      return;
    }
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setHeight(vv.height);
    // Sync once on (re-)enable in case the keyboard is already open, then track.
    update();
    vv.addEventListener('resize', update);
    return () => vv.removeEventListener('resize', update);
  }, [enabled]);

  return height;
}
